<template>
	<div>
		<LoadingIcon v-if="isLoading" alignment="center" />
		<div v-if="settings">
			<div class="tabs">
				<div
					role="button"
					aria-label="Contact"
					@click="setView('Contact')"
					class="tab"
					:class="{ selected: view === 'Contact' }"
					data-test="contact"
				>
					Contact
				</div>
				<div
					role="button"
					aria-label="Loan"
					@click="setView('Loan')"
					class="tab"
					:class="{ selected: view === 'Loan' }"
					data-test="loan"
				>
					Loan
				</div>
			</div>
			<div v-if="!showLoans">
				<div
					v-for="(preference, index) in settings.preferences"
					class="alert-container"
					data-test="setting"
					:key="index"
					@click="handleConfigureAlerts(preference)"
				>
					<div class="alert-type-container">
						<AlertTypeIcon :alertType="preference.type.desc" />
						<div class="alert-type" data-test="alert-type">
							<span
								role="button"
								:aria-label="preference.type.desc"
								class="type"
								data-test="button"
							>
								{{ preference.type.desc }}
							</span>
							<span>
								{{
									alertStatus({
										optInInd: preference.optInInd,
									})
								}}
							</span>
						</div>
					</div>
				</div>
			</div>
			<LoansList
				v-if="showLoans"
				view="settings"
				:leadId="contactId"
				@onLoanClick="$emit('onLoanClick', $event, 'Settings')"
			/>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex';
import { sortBy } from 'lodash';

import AlertTypeIcon from '../AlertTypeIcon.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import LoansList from './LoansList.vue';
import { VIEWS, CONTACT_LEVEL_ALERTS } from './constants';
import leadsService from '@/services/contacts/leads-service.js';

export default {
	name: 'ContactSettings',
	components: { AlertTypeIcon, LoadingIcon, LoansList },
	props: {
		contactId: Number,
	},
	data() {
		return {
			settings: null,
			isLoading: false,
			view: 'Contact',
		};
	},
	computed: {
		...mapState({
			roles: (state) => state.profile.roles,
		}),
		canUpdateCompanyProfile() {
			return this.roles.some(
				(role) => role === 'BrokerOwner' || role === 'MarketingManager',
			);
		},
		showLoans() {
			return this.view === 'Loan';
		},
	},
	methods: {
		getNextView(alert) {
			return CONTACT_LEVEL_ALERTS.includes(alert)
				? VIEWS.ALERT_SETTINGS
				: VIEWS.LOANS_LIST;
		},
		handleConfigureAlerts(alertPreference) {
			const { loanId, leadId } = this.settings;
			const view = this.getNextView(alertPreference.type.desc);
			this.$emit('configureAlerts', {
				alertPreference,
				loanId,
				leadId,
				view,
			});
		},
		alertStatus({ optInInd }) {
			if (optInInd === false) {
				return 'Disabled';
			} else {
				return 'Enabled';
				// deepcode ignore JavascriptDeadCode: This is wrong, why would a closing brace be unreachable code?
			}
		},
		setView(view) {
			this.view = view;
		},
		filteredPreferences(preferences) {
			const contactPreferences = ['CreditWatch', 'MarketWatch', 'Birthday'];
			return preferences.filter((preference) =>
				contactPreferences.includes(preference.typeCode),
			);
		},
		sortedPreferences(preferences) {
			return sortBy(
				this.filteredPreferences(preferences),
				(preferences) => preferences.type.desc,
			);
		},
	},
	async created() {
		this.isLoading = true;
		const response = await leadsService.getPreferencesByLeadId(this.contactId);
		this.settings = {
			...response,
			...{ preferences: this.sortedPreferences(response.preferences) },
		};
		this.isLoading = false;
	},
};
</script>

<style lang="scss" scoped>
.tabs {
	display: flex;
	align-items: center;
	border-bottom: 1px solid $border-color-light;
	@include rem('height', 60px);

	.tab {
		font-weight: 500;
		cursor: pointer;
		@include rem('margin-left', 20px);

		&.selected {
			color: $brand-blue;
			border-bottom: 2px solid $brand-blue;
			@include rem('padding', 20px 0 18px 0);
		}
	}
}

.alert-container {
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	align-items: center;
	@include rem('padding', 18px 20px);
	border-bottom: 1px solid $border-color-light;
}

.alert-type-container {
	display: flex;
}

.alert-type {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	@include rem('margin-left', 15px);
}

.type {
	color: $brand-blue;
	font-weight: 500;
	cursor: pointer;
}
</style>
